import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  addBot,
  createQQGroup,
  deleteBot,
  deleteQQGroup,
  getAvailableGroupList,
  getBotList,
  getGroupList,
  getPendingMembers,
  moveQQGroupSortOrder,
  refreshGroupList,
  reconnectQQBot,
  getReviewLogs,
  reviewMember,
  updateQQBot,
  updateGroupConfig,
  type CreateQQGroupInput,
  type UpdateGroupConfigInput,
} from '@/api/adminApi'

export const qqBotKeys = {
  all: ['qq-bot'] as const,
  bots: () => [...qqBotKeys.all, 'bots'] as const,
  groups: () => [...qqBotKeys.all, 'groups'] as const,
  pendingMembers: () => [...qqBotKeys.all, 'pending-members'] as const,
  reviewLogs: () => [...qqBotKeys.all, 'review-logs'] as const,
}

export function useQQBotList() {
  return useQuery({
    queryKey: qqBotKeys.bots(),
    queryFn: getBotList,
  })
}

export function useQQGroupList() {
  return useQuery({
    queryKey: qqBotKeys.groups(),
    queryFn: () => getGroupList(),
  })
}

export function useAvailableQQGroups(botId?: string) {
  return useQuery({
    queryKey: [...qqBotKeys.groups(), 'available', botId || 'none'],
    queryFn: () => {
      if (!botId) {
        throw new Error('缺少机器人 ID')
      }
      return getAvailableGroupList(botId)
    },
    enabled: Boolean(botId),
  })
}

export function usePendingMembers(groupId?: string) {
  return useQuery({
    queryKey: [...qqBotKeys.pendingMembers(), groupId || 'all'],
    queryFn: () => getPendingMembers({ groupId }),
  })
}

export function useReviewLogs(groupId?: string) {
  return useQuery({
    queryKey: [...qqBotKeys.reviewLogs(), groupId || 'all'],
    queryFn: () => getReviewLogs({ groupId }),
  })
}

export function useAddQQBot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addBot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.bots() })
    },
  })
}

export function useDeleteQQBot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (botId: string) => deleteBot(botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.bots() })
    },
  })
}

export function useUpdateQQBot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      botId,
      input,
    }: {
      botId: string
      input: { name?: string; wsUrl?: string; accessToken?: string }
    }) => updateQQBot(botId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.bots() })
    },
  })
}

export function useReconnectQQBot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (botId: string) => reconnectQQBot(botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.bots() })
      queryClient.invalidateQueries({ queryKey: qqBotKeys.groups() })
    },
  })
}

export function useUpdateQQGroupConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      groupId,
      input,
    }: {
      groupId: string
      input: UpdateGroupConfigInput
    }) => updateGroupConfig(groupId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.groups() })
    },
  })
}

export function useAddQQGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateQQGroupInput) => createQQGroup(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.groups() })
      queryClient.invalidateQueries({ queryKey: [...qqBotKeys.groups(), 'available'] })
    },
  })
}

export function useDeleteQQGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (groupId: string) => deleteQQGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.groups() })
    },
  })
}

export function useMoveQQGroupSortOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      groupId,
      direction,
    }: {
      groupId: string
      direction: 'up' | 'down'
    }) => moveQQGroupSortOrder(groupId, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.groups() })
    },
  })
}

export function useRefreshQQGroupList() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (botId: string) => refreshGroupList(botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.groups() })
    },
  })
}

export function useReviewMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      requestId,
      approve,
      reason,
    }: {
      requestId: string
      approve: boolean
      reason?: string
    }) => reviewMember({ requestId, approve, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qqBotKeys.pendingMembers() })
      queryClient.invalidateQueries({ queryKey: qqBotKeys.reviewLogs() })
    },
  })
}
